import React, { useEffect } from 'react';
import { Download, X } from 'lucide-react';
import { Dialog, DialogContent } from '../ui/dialog';
import { Button } from '../ui/button';
import { formatINR } from '../../lib/format';
import { downloadPayslip } from '../../lib/downloadPayslip';

export const PayslipModal = ({ slip, onClose }) => {
  useEffect(() => {
    if (slip?.autoDownload) {
      downloadPayslip(slip);
      onClose();
    }
  }, [slip, onClose]);

  if (!slip || slip.autoDownload) return null;

  const totalDed = (slip.pf || 0) + (slip.esi || 0) + (slip.lop || 0);

  return (
    <Dialog open={!!slip} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0" data-testid="payslip-modal">
        <div className="relative">
          <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 rounded-md hover:bg-slate-100 flex items-center justify-center text-slate-500 z-10">
            <X className="w-4 h-4" />
          </button>

          <div className="px-6 pt-6 pb-5 border-b border-slate-200 bg-gradient-to-br from-slate-50 to-white">
            <div className="text-2xl font-semibold text-slate-900 font-heading">{slip.businessName}</div>
            <div className="text-xs uppercase tracking-widest text-slate-500 mt-1">Payslip · {slip.month}</div>
          </div>

          <div className="px-6 py-5 grid grid-cols-2 gap-y-3 gap-x-4 text-sm border-b border-slate-200">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Employee</div>
              <div className="font-medium text-slate-900 mt-0.5">{slip.name}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Employee ID</div>
              <div className="font-medium text-slate-900 mt-0.5 font-mono">{slip.empId}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Department</div>
              <div className="font-medium text-slate-900 mt-0.5">{slip.department || '—'}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Designation</div>
              <div className="font-medium text-slate-900 mt-0.5">{slip.type || 'Full-time'}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Days Present</div>
              <div className="font-medium text-slate-900 mt-0.5 font-mono">{slip.daysPresent}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Pay Period</div>
              <div className="font-medium text-slate-900 mt-0.5">{slip.month}</div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-0 border-b border-slate-200">
            <div className="p-6 border-r border-slate-200">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-medium mb-3">Earnings</div>
              <div className="space-y-2 text-sm">
                {[
                  ['Basic', slip.basic],
                  ['HRA', slip.hra],
                  ['Allowances', slip.allowances],
                  ['Overtime', slip.overtime],
                  ['Bonus', slip.bonus],
                ].map(([l, v]) => (
                  <div key={l} className="flex justify-between">
                    <span className="text-slate-600">{l}</span>
                    <span className="font-mono text-slate-900">{v ? formatINR(v) : '—'}</span>
                  </div>
                ))}
                <div className="flex justify-between pt-2 border-t border-slate-200 font-semibold">
                  <span className="text-slate-900">Gross</span>
                  <span className="font-mono text-slate-900">{formatINR(slip.gross)}</span>
                </div>
              </div>
            </div>
            <div className="p-6">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-medium mb-3">Deductions</div>
              <div className="space-y-2 text-sm">
                {[
                  ['PF (12% of Basic)', slip.pf],
                  ['ESI (0.75%)', slip.esi],
                  ['LOP Deduction', slip.lop],
                ].map(([l, v]) => (
                  <div key={l} className="flex justify-between">
                    <span className="text-slate-600">{l}</span>
                    <span className="font-mono text-red-600">{v ? `−${formatINR(v)}` : '—'}</span>
                  </div>
                ))}
                <div className="flex justify-between pt-2 border-t border-slate-200 font-semibold">
                  <span className="text-slate-900">Total</span>
                  <span className="font-mono text-red-700">−{formatINR(totalDed)}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="px-6 py-5 bg-[#2E6DA4] text-white flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-blue-100">Net Pay</div>
              <div className="text-xs text-blue-200 mt-0.5">Take-home this month</div>
            </div>
            <div className="text-3xl font-bold font-mono" data-testid="payslip-net-pay">{formatINR(slip.net)}</div>
          </div>

          <div className="px-6 py-4 flex items-center justify-between bg-slate-50">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Generated by MicroPay · System generated, no signature required</div>
            <Button 
              onClick={() => downloadPayslip(slip)} 
              className="bg-[#2E6DA4] hover:bg-[#255a88] text-white" 
              data-testid="payslip-download-btn"
            >
              <Download className="w-4 h-4 mr-1.5" /> Download PDF
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};